import { t } from "@hakili/i18n";
import { useState } from "react";

import type { BankView } from "./partner-banks.js";

/**
 * `PRET-BANKS` (staging, H-2026-014): **the panel, shown and pressed on the bank's sheet.**
 *
 * `lending.banks.active` is membership of the panel , `domain/filter.ts` walks only the banks that carry it , and it defaults
 * `false` for a bank the modal opens. Nothing showed it and nothing set it, so a bank Partenaires called « Active » (its
 * accounts) never reached Prêt, silently. This block says the state on its own labelled line (the chip above stays about accounts)
 * and carries the act: `PUT /banks/{id}/panel`, row `ouvrirOrganisation` , the door writes the audit line naming the actor.
 *
 * Without the right the state is still said and no control is drawn: it is not her door (`D-1019-2`). A refusal is the door's own
 * words (A47), and the state on screen is only ever what the door last answered.
 */
export function BankPanelBlock(props: {
  readonly bank: BankView;
  readonly mayAdmit: boolean;
  readonly record: ((bankId: string, active: boolean) => Promise<void>) | undefined;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const { bank, record } = props;
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // « Retirer du panel » costs the bank every future file: it asks first, naming that (admitting is not a loss and asks nothing).
  const [confirming, setConfirming] = useState(false);

  const press = (active: boolean): void => {
    if (record === undefined) return;
    void (async () => {
      setBusy(true);
      setRefusal(null);
      try {
        await record(bank.id, active);
        setConfirming(false);
        props.onSaved();
      } catch (failure) {
        setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <section data-testid="fiche-panel" className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
      <h2 className="mb-2 text-sm font-semibold">{t("console.partenaires.panelTitre")}</h2>
      <p
        className="text-sm text-gray-700"
        data-testid="fiche-panel-etat"
        data-panel={bank.active ? "oui" : "non"}
      >
        {bank.active ? t("console.partenaires.panelOui") : t("console.partenaires.panelNon")}
      </p>
      {!props.mayAdmit || bank.mayAdmitToPanel === false || record === undefined ? null : (
        <div className="mt-2">
          {bank.active ? (
            confirming ? (
              <div data-testid="fiche-panel-retirer-confirmation" className="rounded-xl bg-gray-50 px-4 py-3">
                <p className="text-sm text-gray-700">
                  {t("console.partenaires.panelRetirerConfirmation").replace("{nom}", bank.name)}
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    data-testid="fiche-panel-retirer-confirmer"
                    disabled={busy}
                    onClick={() => {
                      press(false);
                    }}
                    className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
                  >
                    {t("console.partenaires.panelRetirerConfirmer")}
                  </button>
                  <button
                    type="button"
                    data-testid="fiche-panel-retirer-annuler"
                    onClick={() => {
                      setConfirming(false);
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
                  >
                    {t("console.partenaires.panelRetirerAnnuler")}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                data-testid="fiche-panel-retirer"
                disabled={busy}
                onClick={() => {
                  setConfirming(true);
                }}
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                {t("console.partenaires.panelRetirer")}
              </button>
            )
          ) : (
            <button
              type="button"
              data-testid="fiche-panel-admettre"
              disabled={busy}
              onClick={() => {
                press(true);
              }}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
            >
              {t("console.partenaires.panelAdmettre")}
            </button>
          )}
        </div>
      )}
      {refusal === null ? null : (
        <p role="alert" data-testid="fiche-panel-refus" className="mt-2 text-xs text-rose-600">
          {refusal}
        </p>
      )}
    </section>
  );
}
